<template>
  <div class="container">
    <div
      class="row"
    >
      <div class="col-12 mx-auto">
        <nav
          class="nav nav-pills nav-fill"
        >
          <span
            v-for="(item, index) in keywords"
            :key="index"
            class="nav-item pt-1 pb-1"
            :class="item.keyword === selectedKeyword ? 'active' : ''"
            @click="updateSelectedKeyword(item.keyword)"
          >

            <a
              class="mb-0 nav-link d-inline"
              :href="'https://www.google.com/search?query='+item.keyword"
              target="_blank"
            >
              <i class="fa fa-link pr-1" />
              <b>{{ item.keyword }}</b>
            </a>

            <div v-if="fetchingKeyword(item.keyword)">
              <preloader />
            </div>

            <div v-else>
              <span class="d-none d-md-block">
                Search Volume
                <b
                  v-if="item.sv"
                  v-text="item.sv"
                />
                <b v-else>-</b>
                | CPC
                <b
                  v-if="item.cpc"
                >
                  {{ '$'+formatMoney(item.cpc) }}
                </b>
                <b v-else>-</b>
              </span>
            </div>

          </span>
        </nav>
        <div class="results">
          <Card :padding="isMobile">
            <div v-if="hasError()">
              <div class="text-center">
                <img
                  width="320"
                  src="@/assets/img/error.svg"
                  alt="Something went wrong"
                  class="mb-2"
                >
                <h5>
                  Sorry, something went wrong.
                </h5>
              </div>
            </div>
            <div
              v-else-if="fetchingResults(selectedKeyword) || !selectedKeyword"
            >
              <vcl-table
                :rows="4"
                :columns="8"
              />
            </div>
            <div v-else>
              <div v-if="isMobile">
                <competitors-mobile-table
                  :results="results[selectedKeyword]"
                  :selected-keyword="selectedKeyword"
                />
              </div>
              <div v-else>
                <competitors-table
                  :url="url"
                  :results="results[selectedKeyword]"
                  :selected-keyword="selectedKeyword"
                  @on-refetch="refetchKeyword"
                />
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div class="col-sm-6 mx-auto">
        <div class="mt-2">
          <button
            class="btn btn-lg btn-gradient-primary btn-oval btn-block py-2"
            @click="showConsultationModal()"
          >
            Schedule a consultation
          </button>
        </div>
        <div class="d-flex align-items-center justify-content-center mt-5 mt-md-2">
          <button
            class="btn btn-lg btn-oval btn-block position-relative pr-0 pt-0 pb-0"
            :disabled="generatingPDF"
            @click="generatePDF"
          >
            <span
              v-if="generatingPDF"
              class="spinner"
            />
            <span class="text-uppercase mobile-pdf">
              <img
                class=""
                src="@/assets/img/pdf.svg"
                alt="PDF"
              >
              Download a PDF
            </span>
          </button>
          <a
            class="w-100 d-flex clear-button"
            href="/"
          >
            <i
              class="fa fa-undo"
              aria-hidden="true"
            />
            <span class="ml-1 text-dark">Clear and start again</span>
          </a>
        </div>
      </div>

      <ScheduleConsultationModal
        :show-modal="isConsulationModalVisible"
        @on-close="closeModal"
      />
    </div>
  </div>
</template>
<script>
import { captureException } from '@sentry/browser';
import { mapState, mapActions } from 'vuex';
import { VclTable } from 'vue-content-loading';
import CompetitorsTable from '@/components/CompetitorsTable';
import CompetitorsMobileTable from '@/components/CompetitorsMobileTable';
import ScheduleConsultationModal from '@/components/ScheduleConsultationModal';
import Preloader from '@/components/Preloader';
import otmService from '@/services/otmService';

const isMobile = require('ismobilejs')

export default {

  head: {
    title: {
      inner: 'SEO Analyzer: Free Analysis Tool & Audit Report'
    },
    meta: [
      { name: 'robots', content: "noindex,nofollow" }
    ],
    link: [
      { rel: 'canonical', href: 'https://www.seo-browser.com', id: 'canonical' },
    ]
  },

  components: {
    CompetitorsTable,
    CompetitorsMobileTable,
    VclTable,
    ScheduleConsultationModal,
    Preloader
  },

  data() {
    return {
      isMobile: isMobile.any,
      selectedList: null,
      generatingPDF: false,
      isConsulationModalVisible: false,
      errors: {},
    }
  },

  computed: {

    ...mapState('app', ['url','currentStep','competitors','error']),

    ...mapState('keyword', ['additionalResults', 'fetchingKeywords', 'fetchingKeywordResults', 'keywords','selectedKeyword', 'results']),

  },

  mounted() {
    this.isMobile = isMobile.any

    const { domain } = this.$route.params;

    this.updateURL(domain);

    // If no keywords in vuex state, try to read them from url query
    if (Object.keys(this.keywords).length === 0) {
      const { query } = this.$route;

      if (query.hasOwnProperty('keywords')) {
        const queryString = this.getQueryVariable('keywords');

        const keywords = queryString.split(',');

        if(keywords.length > 3) {
          this.$router.push({ name: 'keywords' })
          return;
        }

        this.updateKeywords(keywords);
      }

      else {
        this.$router.push({ name: 'keywords' })
        return;
      }
    }

    this.queueKeywords();
  },

  methods: {

    ...mapActions('app', ['updateURL']),

    ...mapActions('keyword', [
      'updateKeywords',
      'updateSelectedKeyword', 
      'fetchSingleResultData',
      'fetchKeywordResults',
      'fetchResultsData',
      'fetchSummary',
      'updateSelectedKeyword',
      ]),

    hasError() {
      if(this.selectedKeyword && this.errors.hasOwnProperty(this.selectedKeyword)) {
        return true;
      }

      return false;
    },

    async asyncForEach(array, callback) {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
      }
    },

    async queueKeywords() {
      const newKey = Object.keys(this.keywords)
      const oldKey = Object.keys(this.results)

      // This function is used to identify if some of the keywords have been changed,
      // If they are changed, then it fetches API only from the changed keywords.
      if (this.arrayDiff(newKey, oldKey).length === 0) 
        return;
      
      const changedKeys = this.arrayDiff(newKey, oldKey)

      this.asyncForEach(changedKeys, async (keyword) => {

        try {
          const results = await this.fetchKeywordResults({ keyword, domain: this.url });

          if(!this.selectedKeyword)
            this.updateSelectedKeyword(keyword);

          this.fetchSummary(keyword)

          this.fetchResultsData(results);

        } catch (error) {
          this.updateSelectedKeyword(keyword);

          this.errors = { ...this.errors, [keyword]: {...error} }
        }
      })
    },

    onChangeKeyword(event) {
      this.updateSelectedKeyword(event.target.value)
    },

    async refetchKeyword(data) {

      const { url, keyword, uuid } = data;

      this.fetchSingleResultData({ item: { key: keyword, uuid }, url });
    },

    fetchingKeyword(keyword) {
      return this.fetchingKeywords.includes(keyword)
    },

    fetchingResults(keyword) {
      return this.fetchingKeywordResults.includes(keyword)
    },

    async generatePDF() {
      this.generatingPDF = true;

      try {
        const { url, keywords, results, additionalResults } = this;

        let tableData = {};

        for (let i = 0; i < Object.keys(keywords).length; i++) {

          const key = Object.keys(keywords)[i];

          const rows = results[key].map(item => {

            if (!additionalResults.hasOwnProperty(item.uuid) || !additionalResults[item.uuid].hasOwnProperty('domain'))
              return {};

            const { age, backlinks, content_count, page_count, time_to_load } = additionalResults[item.uuid].domain;

            return {
                position: item.position,
                domain: item.host,
                content_count,
                backlinks,
                age: this.humanise(age),
                page_count,
                time_to_load: `${(time_to_load*10).toFixed(2)}s`,
                isActiveRow: this.formatUrl(item.url) === this.formatUrl(url),
            }

          })

          tableData = { ...tableData, [key]: { name: key, sv: keywords[key].sv, cpc: '$'+this.formatMoney(keywords[key].cpc), rows } }
        }

        const { data } = await otmService.generatePDF({ keywords: Object.keys(keywords), tableData });
        
        //Move this somewhere else
        const today = new Date();
        const month = ('0' + (today.getMonth() + 1)).slice(-2);
        const date = ('0' + today.getDate()).slice(-2);
        const year = today.getFullYear();
        const formattedDate = month + '-' + date + '-' + year;
        const { domain } = this.$route.params;

        this.downloadFile(data,`${domain}-${formattedDate}.pdf`);
        this.generatingPDF = false;

      } catch (error) {
        captureException(error);
        this.generatingPDF = false;
      }
    },

    downloadFile (blob, fileName) {
      const link = document.createElement('a');
      // create a blobURI pointing to our Blob
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      // some browser needs the anchor to be in the doc
      document.body.append(link);
      link.click();
      link.remove();
      // in case the Blob uses a lot of memory
      window.addEventListener('focus', () => URL.revokeObjectURL(link.href), {once:true});
    },

    showConsultationModal() {
      this.isConsulationModalVisible = true;
    },

    closeModal() {
      this.isConsulationModalVisible = false;
    },

  }
}
</script>

<style scoped lang="scss">
.box-container {
  .card {
    border-radius: 0;
  }

  .fa-exclamation-circle {
    font-size: 5rem;
    align-items: center;
    justify-content: center;
    display: flex;
    margin-bottom: 1rem;
    margin-top: 1rem;
  }
}

.nav.nav-pills {
  .nav-item {
    border-radius: 0;
    background-color: #e7e7e7;
    border-bottom: 4px solid rgba(38, 38, 38, 0.1);
    position: relative;
    cursor: pointer;
    p,
    span {
      color: #000000;
    }
    .nav-link {
      color: #000;
      &:hover{
      text-decoration: underline;
      }
    }
    &.active {
      box-shadow: 0 4px 7px rgba(182, 182, 182, 0.4);
      background-image: linear-gradient(270deg, #3caef3 0%, #266ce7 100%);
      border-bottom: none;
      .nav-link {
        color: #fff;
      }
      p,
      span {
        color: #fff;
      }
      &::after {
        content: '';
        position: absolute;
        bottom: -2rem;
        z-index: 10;
        width: 0;
        height: 0;
        left: 50%;
        border-left: 20px solid transparent;
        border-right: 20px solid transparent;
        border-top: 20px solid #328eee;
      }
    }
  }
  > span + span {
    margin-left: 2rem; // 2px by default
  }
}
.error-message {
  font-size: 13px;
  text-align: center;
  margin: auto;
}

/*Spinner*/
.spinner {
	display: block;
	width: 34px;
	height: 34px;
	position: absolute;
	top: 25px;
  margin:0 auto;
  left:0;
  right:0;
	background: transparent;
	box-sizing: border-box;
	border-top: 4px solid rgba(8, 98, 212, 0.65);
	border-left: 4px solid transparent;
	border-right: 4px solid transparent;
	border-bottom: 4px solid transparent;
	border-radius: 100%;
  animation-delay:0.5s;
	animation: spin 0.6s ease-out infinite;
  transition:all ease 0.5s;
}

@keyframes spin {
	100% {transform: rotate(360deg)}
}

.loader {
  border: 5px solid #f3f3f3;
  border-radius: 50%;
  border-top: 5px solid #328eee;
  width: 50px;
  height: 50px;
  -webkit-animation: spin 1s linear infinite; /* Safari */
  animation: spin 1s linear infinite;
  margin: 2rem auto;
}

/* Safari */
@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
/*Spinner END*/

@media only screen and (max-width: 991.98px) {
  .nav.nav-pills {
    flex-wrap: nowrap;
    .nav-item.active::after {
      content: none;
    }
  }
  .vue-form-wizard .wizard-tab-content {
    .box-container {
      margin-left: 0;
      margin-right: 0;
    }
  }
}
@media only screen and (max-width: 767.98px) {
  .box-container .table-responsive {
    display: block;
  }
}
.card.small-card {
  label {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
  select {
    box-shadow: 2px 1px 9px -3px #ccc;
    height: 42px;
    font-size: 16px;
    font-weight: 500;
    color: #2d2e35;
    text-transform: capitalize;
    border-radius: 0.3rem;
  }

}
.clear-button {
  border-left: 2px solid #A3A3A3;
  padding-top: 1rem;
  padding-bottom: 1rem;
  border-radius: 0;
  padding-left: 2rem;
  position: relative;
  top: -5px;
  background: transparent;
  border-right: none;
  border-bottom: none;
  border-top: none;
  .fa-undo {
    color: #C10102;
    font-size: 2.3rem;
  }
}
@media (max-width: 700px) {
  .mobile-pdf {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
  }
  .clear-button {
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>